import React, { useEffect, useMemo, useState, type FC } from "react";
import { DeleteOutlined, LeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Spin } from "antd";
import { Button, DataTable, StatusBadge } from "../../../../components/shared";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import { SIDER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { convertToUserTimezone } from "../../../../utils/dateService";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";
import { useGetSeries } from "../../../../queries/dynamicBonuses/useGetSeries";
import { useDeleteSeries } from "../../../../queries/dynamicBonuses/useDeleteSeries";
import {
  useSeriesTransition,
  type SeriesTransitionAction,
} from "../../../../queries/dynamicBonuses/useSeriesTransition";
import { useListSeriesRuns } from "../../../../queries/dynamicBonuses/useListSeriesRuns";
import { campaignStatusBadgeProps, EMPTY_DISPLAY } from "../constants";
import { compareIsoDates } from "../listTableSorters";
import DeleteDynamicBonusModal from "../controls/DeleteDynamicBonusModal";
import { DYNAMIC_BONUS_CAMPAIGN_STATUS, type RunOut } from "../types";
import BrazePayloadModal from "./BrazePayloadModal";
import wizardStyles from "../../../Affiliates/NewAffiliate/NewAffiliate.module.css";
import styles from "../stylesDynamicBonuses.module.css";

/** Braze brand purple — same value as `.brazeTypeBadge` (not an operator token). */
const BRAZE_PURPLE = "#801dd7";

type SeriesLocationState = {
  openBrazePayload?: boolean;
};

type SeriesDeleteModalProps = {
  seriesId: string;
  seriesName: string;
  onClose: () => void;
  onDeleted: () => void;
};

const SeriesDeleteModal: FC<SeriesDeleteModalProps> = ({
  seriesId,
  seriesName,
  onClose,
  onDeleted,
}) => {
  const deleteMutation = useDeleteSeries(seriesId);

  return (
    <DeleteDynamicBonusModal
      open
      resourceType="series"
      resourceName={seriesName}
      isPending={deleteMutation.isPending}
      onClose={onClose}
      onConfirm={() =>
        deleteMutation.mutate(undefined, { onSuccess: onDeleted })
      }
    />
  );
};

const fmt = (iso: string | null | undefined): string =>
  iso ? convertToUserTimezone(iso, "YYYY-MM-DD HH:mm") : EMPTY_DISPLAY;

// Series never expire, so there is no "expire" transition — only activate/pause.
type TransitionButton = { action: SeriesTransitionAction; label: string };

const getTransitions = (status: string): TransitionButton[] => {
  const transitions: Record<string, TransitionButton[]> = {
    draft: [
      {
        action: "activate",
        label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_ACTIVATE,
      },
    ],
    active: [
      {
        action: "pause",
        label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_PAUSE,
      },
    ],
    paused: [
      {
        action: "activate",
        label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_ACTIVATE,
      },
    ],
    expired: [],
    archived: [],
  };
  return transitions[status] ?? [];
};

const buildCampaignColumns = () => [
  {
    title: AppStrings.NAME,
    dataIndex: "name",
    key: "name",
    render: (v: string) => <span className={styles.campaignName}>{v}</span>,
  },
  {
    title: AppStrings.STATUS,
    dataIndex: "status",
    key: "status",
    width: 120,
    render: (v: RunOut["status"]) => (
      <StatusBadge {...campaignStatusBadgeProps(v)}>{v}</StatusBadge>
    ),
  },
  {
    title: AppStrings.PAM_TEMPLATES_CREATED,
    dataIndex: "created_at",
    key: "created_at",
    width: 180,
    render: (v: string) => fmt(v),
  },
  {
    title: AppStrings.PAM_CAMPAIGNS_COLUMN_ENDS,
    dataIndex: "ends_at",
    key: "ends_at",
    width: 180,
    render: (v: string | null) => fmt(v),
  },
];

const SeriesDetailPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { seriesId } = useParams<{ seriesId: string }>();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isBrazePayloadOpen, setIsBrazePayloadOpen] = useState(false);

  // After create, the wizard navigates here with openBrazePayload so operators
  // can copy the webhook body immediately. Clear the state so refresh/back
  // does not reopen the modal.
  useEffect(() => {
    const state = location.state as SeriesLocationState | null;
    if (!state?.openBrazePayload) return;
    setIsBrazePayloadOpen(true);
    navigate(".", { replace: true, state: {} });
  }, [location.state, navigate]);

  const { data: series, isLoading, isError } = useGetSeries(seriesId ?? "");
  usePageTitle(
    isLoading
      ? AppStrings.PAM_DYNAMIC_BONUS_SERIES_FALLBACK
      : (series?.name ?? AppStrings.PAM_DYNAMIC_BONUS_SERIES_FALLBACK),
  );
  const transitionMutation = useSeriesTransition(seriesId ?? "");
  const runsQuery = useListSeriesRuns(seriesId ?? "");

  const sortedCampaigns = useMemo(() => {
    const items = runsQuery.data?.items ?? [];
    return [...items].sort((a, b) => compareIsoDates(b.created_at, a.created_at));
  }, [runsQuery.data?.items]);

  const goToCampaign = (run: RunOut) =>
    navigate(
      NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGN_DETAIL.replace(
        ":campaignId",
        run.campaign_id,
      ),
    );

  const goToEdit = () => {
    if (!seriesId) return;
    navigate(
      NAVIGATION_ROUTES.DYNAMIC_BONUS_SERIES_EDIT.replace(":seriesId", seriesId),
    );
  };

  const transitions = series ? getTransitions(series.status) : [];

  return (
    <>
      <FeatureFlagGuard feature="dynamicBonusesExternal" mode="route">
        <div className={wizardStyles.page}>
          <div className={wizardStyles.header}>
            <button
              type="button"
              className={wizardStyles.backButton}
              onClick={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS)}
              aria-label={AppStrings.PAM_DYNAMIC_BONUS_BACK_TO_CAMPAIGNS}
            >
              <span className={wizardStyles.backButtonIcon}>
                <LeftOutlined />
              </span>
            </button>
            {series && (
              <StatusBadge {...campaignStatusBadgeProps(series.status)}>
                {series.status}
              </StatusBadge>
            )}
            <div className={styles.headerActions}>
              {/* No dedicated feature flag by design: this is a read-only
                  operator helper (copy Braze JSON) inside the existing
                  dynamicBonusesExternal-gated series flow. It does not mutate
                  campaign state, change user-facing behavior, or expand access,
                  so a second flag here would be a redundant always-on duplicate. */}
              {series && (
                <Button
                  color={BRAZE_PURPLE}
                  onClick={() => setIsBrazePayloadOpen(true)}
                >
                  {AppStrings.PAM_DYNAMIC_BONUS_BRAZE_PAYLOAD_BUTTON}
                </Button>
              )}
              {series && (
                <Button intent="secondary" onClick={goToEdit}>
                  {AppStrings.PAM_DYNAMIC_BONUS_SERIES_EDIT}
                </Button>
              )}
              {series?.status === DYNAMIC_BONUS_CAMPAIGN_STATUS.DRAFT && (
                <Button
                  intent="danger"
                  iconLeft={<DeleteOutlined />}
                  onClick={() => setIsDeleteOpen(true)}
                >
                  {AppStrings.PAM_DYNAMIC_BONUS_DELETE_ACTION}
                </Button>
              )}
              {transitions.map(({ action, label }) => (
                <Button
                  key={action}
                  intent="primary"
                  loading={transitionMutation.isPending}
                  onClick={() => transitionMutation.mutate(action)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {isLoading && <Spin />}
          {(isError || !seriesId) && !isLoading && (
            <p className={styles.listError}>
              {AppStrings.PAM_CAMPAIGNS_SERIES_LOAD_ERROR}
            </p>
          )}
          {series && (
            <div className={styles.campaignsTableSection}>
              <h2 className={styles.sectionTitle} style={{ marginBottom: 12 }}>
                {runsQuery.data?.count != null
                  ? formatString(AppStrings.PAM_DYNAMIC_BONUS_SERIES_CAMPAIGNS_COUNT, {
                      count: String(runsQuery.data.count),
                    })
                  : AppStrings.PAM_CAMPAIGNS}
              </h2>
              {runsQuery.isError && (
                <p className={styles.listError}>
                  {AppStrings.PAM_CAMPAIGNS_LOAD_ERROR}
                </p>
              )}
              {!runsQuery.isError && runsQuery.data?.count === 0 && (
                <div
                  className={`${styles.infoBox} ${styles.seriesEmptyCallout}`}
                  role="note"
                >
                  {AppStrings.PAM_DYNAMIC_BONUS_SERIES_EMPTY}
                </div>
              )}
              <DataTable<RunOut>
                rowKey="campaign_id"
                className={styles.campaignsTable}
                columns={buildCampaignColumns()}
                dataSource={sortedCampaigns}
                loading={runsQuery.isLoading}
                pagination={false}
                onRow={(record) => ({
                  onClick: () => goToCampaign(record),
                  style: { cursor: "pointer" },
                })}
              />
            </div>
          )}
          {series && isDeleteOpen && (
            <SeriesDeleteModal
              seriesId={series.series_campaign_id}
              seriesName={series.name}
              onClose={() => setIsDeleteOpen(false)}
              onDeleted={() =>
                navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS)
              }
            />
          )}
          {series && isBrazePayloadOpen && (
            <BrazePayloadModal
              open
              seriesId={series.series_campaign_id}
              onClose={() => setIsBrazePayloadOpen(false)}
            />
          )}
        </div>
      </FeatureFlagGuard>
    </>
  );
};

export default SeriesDetailPage;
